import { BattleCardBreakdown, ColorRank, GameCard } from "../types/battle"

export const RANK_BASE: Record<ColorRank, number> = {
  WHITE: 100,
  GREEN: 1200,
  BLUE: 4500,
  PURPLE: 13500,
  ORANGE: 37000,
  RED: 110000,
}

export const MAX_LEVEL: Record<ColorRank, number> = {
  WHITE: 10,
  GREEN: 20,
  BLUE: 30,
  PURPLE: 40,
  ORANGE: 50,
  RED: 100,
}

export const RANK_ORDER: ColorRank[] = [
  "WHITE",
  "GREEN",
  "BLUE",
  "PURPLE",
  "ORANGE",
  "RED",
]

/** Шанс успеха слияния по рангу */
export const FUSE_SUCCESS_RATE: Record<ColorRank, number> = {
  WHITE: 1,
  GREEN: 0.6,
  BLUE: 0.5,
  PURPLE: 0.3,
  ORANGE: 0.02,
  RED: 0,
}

export const DAILY_QUIZ_REWARD = 50
/** Чакра за друга, завершившего обучение */
export const REFERRAL_REWARD_CHAKRA = 500
export const RANK_EXAM_PASS_SCORE = 8
export const RANK_EXAM_TOTAL = 10
export const RADAR_ENERGY_COST = 10
export const DECK_ENERGY_COST = 15
export const DECK_SIZE = 6
export const DECK_ROUNDS_TO_WIN = 3
export const DECK_PLAY_CARD_TIMEOUT_SEC = 45
export const CHALLENGE_TIMEOUT_SEC = 60
export const RADAR_PTS_MATCH_RANGE = 150
export const RADAR_QUEUE_TIMEOUT_SEC = 120
export const RADAR_CARD_SELECTION_TIMEOUT_SEC = 60
export const RADAR_WIN_PTS = 25
export const RADAR_WIN_COINS = 50
export const RADAR_LOSE_PTS = 15
/** Лимит наград за бои 1 на 1 в сутки (сами бои безлимитны) */
export const QR_BATTLES_DAILY_LIMIT = 3

/** Лимит наград за тренировки в сутки (сами бои безлимитны). UI берёт dailyRewardLimit с API. */
export const TRAINING_DAILY_REWARD_LIMIT = 20
export const TRAINING_COINS_PER_WIN = 50

export function formatTrainingRewardHint(
  remainingRewardedBattles: number,
  coinsPerWin: number
): string {
  if (remainingRewardedBattles > 0) {
    return `Победа: +${coinsPerWin} монет`
  }
  return "Лимит на сегодня. Можно тренироваться без награды"
}

export function getOneOnOneRewardsLeft(oneOnOneBattlesToday: number): number {
  return Math.max(0, QR_BATTLES_DAILY_LIMIT - oneOnOneBattlesToday)
}

/** @deprecated используйте getOneOnOneRewardsLeft */
export function getQrRewardsLeft(qrBattlesToday: number): number {
  return getOneOnOneRewardsLeft(qrBattlesToday)
}

export function formatOneOnOneRewardsLeftLabel(oneOnOneBattlesToday: number): string {
  const left = getOneOnOneRewardsLeft(oneOnOneBattlesToday)
  return `Наград за 1 на 1 сегодня: ${left} из ${QR_BATTLES_DAILY_LIMIT}`
}

/** @deprecated используйте formatOneOnOneRewardsLeftLabel */
export function formatQrRewardsLeftLabel(qrBattlesToday: number): string {
  return formatOneOnOneRewardsLeftLabel(qrBattlesToday)
}

export function formatOneOnOneRewardsExhaustedHint(): string {
  return "Бои доступны, но очки и монеты за сегодня уже не начисляются"
}

/** @deprecated используйте formatOneOnOneRewardsExhaustedHint */
export function formatQrRewardsExhaustedHint(): string {
  return formatOneOnOneRewardsExhaustedHint()
}
export const ONE_ON_ONE_WIN_PTS = 15
export const ONE_ON_ONE_WIN_COINS = 10
export const ONE_ON_ONE_LOSE_PTS = 5
export const ENERGY_REGEN_PER_HOUR = 5
export const DEFAULT_MAX_ENERGY = 100

function pluralPoints(count: number): string {
  const abs = Math.abs(count)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 14) return "очков"
  if (mod10 === 1) return "очко"
  if (mod10 >= 2 && mod10 <= 4) return "очка"
  return "очков"
}

export function formatPointsDelta(delta: number): string {
  const sign = delta >= 0 ? "+" : ""
  return `${sign}${delta} ${pluralPoints(delta)}`
}

export function formatPointsValue(value: number): string {
  return `${value} ${pluralPoints(value)}`
}

export function calcBaseBS(card: Pick<GameCard, "colorRank" | "level" | "basePower">): number {
  return RANK_BASE[card.colorRank] + (card.level - 1) * 50 + card.basePower
}

/** BS для списков/коллекции, когда нет полного BattleCardBreakdown */
export function displayCardBS(
  card: Pick<GameCard, "colorRank" | "level" | "basePower"> & { baseBS?: number }
): number {
  if (typeof card.baseBS === "number" && Number.isFinite(card.baseBS)) {
    return card.baseBS
  }
  return calcBaseBS(card)
}

export function calcEffectiveBS(combat: BattleCardBreakdown): number {
  if (Number.isFinite(combat.effectiveBS)) {
    return combat.effectiveBS
  }

  const baseBS = combat.baseBS ?? 0
  const school = combat.schoolCoefficient ?? 1
  const reborn = combat.rebornMultiplier ?? 1
  const element = combat.elementBonus ?? 0
  const style = combat.styleBonus ?? 0
  const guardianPassive = combat.guardianPassiveBonus ?? 0
  const pet = combat.petBonus ?? 0
  const shard = combat.shardBonus ?? 0
  const ability = combat.abilityBonus ?? 0
  const icePenalty = combat.iceAntiElementPenalty ?? 0

  return Math.round(
    baseBS * school * reborn +
      element +
      style +
      guardianPassive +
      pet +
      shard +
      ability -
      icePenalty
  )
}

export function chakraCost(level: number): number {
  return Math.floor(50 * Math.pow(1.15, level - 1))
}

export function maxLevelForRank(colorRank: ColorRank): number {
  return MAX_LEVEL[colorRank]
}

export function needsExam(card: Pick<GameCard, "level" | "colorRank">): boolean {
  return card.level >= MAX_LEVEL[card.colorRank]
}

export function canUpgrade(
  card: Pick<GameCard, "level" | "colorRank">,
  chakraCharges: number
): boolean {
  if (needsExam(card)) return false
  return chakraCharges >= chakraCost(card.level)
}

export function nextRank(colorRank: ColorRank): ColorRank | null {
  const idx = RANK_ORDER.indexOf(colorRank)
  if (idx < 0 || idx >= RANK_ORDER.length - 1) return null
  return RANK_ORDER[idx + 1]
}

export function fuseSuccessPercent(colorRank: ColorRank): number {
  return Math.round(FUSE_SUCCESS_RATE[colorRank] * 100)
}

export function fuseSuccessPercentWithBooster(
  colorRank: ColorRank,
  useBooster: boolean
): number {
  const base = fuseSuccessPercent(colorRank)
  // Оранж: усилитель запрещён (как на бэке)
  if (!useBooster || colorRank === "ORANGE") return base
  return Math.min(100, base + 10)
}

/** Можно ли применить +10% усилитель слияния к этому рангу. */
export function canUseFuseBoosterOnRank(colorRank: ColorRank): boolean {
  return colorRank !== "ORANGE" && colorRank !== "RED"
}
